// Datos mock — los tipos viven en `src/types`.
// Este archivo se mantiene como fallback durante desarrollo.
// Re-exporta los tipos para no romper imports existentes en las vistas.

import type {
  LicitacionMock,
  LicitacionEstado,
  Prioridad,
  KPISet,
  PipelinePoint,
  CategoriaShare,
  ActividadItem,
  NavUser,
  Tenant,
  Documento,
  Notificacion,
  User,
} from "@/types";

export type { LicitacionMock, LicitacionEstado, Prioridad, NavUser } from "@/types";

// Alias legacy: las vistas del fallback usan el shape mock original.
type Licitacion = LicitacionMock;

const organismos = [
  "Ministerio de Obras Públicas",
  "Hospital Clínico San Borja",
  "Municipalidad de Las Condes",
  "Gendarmería de Chile",
  "JUNAEB",
  "SERVIU Metropolitano",
  "Carabineros de Chile",
  "Universidad de Chile",
  "FONASA",
  "Ministerio de Educación",
  "ENAP",
  "CODELCO",
];

const categorias = [
  "Tecnología y Software",
  "Construcción",
  "Servicios Profesionales",
  "Equipamiento Médico",
  "Mobiliario",
  "Alimentación",
  "Transporte",
  "Consultoría",
  "Seguridad",
  "Mantención",
];

const regiones = [
  "Metropolitana",
  "Valparaíso",
  "Biobío",
  "Antofagasta",
  "La Araucanía",
  "O'Higgins",
];
const responsables = [
  "María González",
  "Carlos Pérez",
  "Andrea Soto",
  "Felipe Rojas",
  "Camila Vidal",
  "Diego Muñoz",
];
const estados: LicitacionEstado[] = [
  "detectada",
  "analisis",
  "viable",
  "preparacion",
  "postulada",
  "evaluacion",
  "adjudicada",
  "perdida",
];

function seeded(i: number) {
  const x = Math.sin(i * 9999) * 10000;
  return x - Math.floor(x);
}

export const licitaciones: Licitacion[] = Array.from({ length: 48 }).map((_, i) => {
  const r = (n: number) => seeded(i + n);
  const monto = Math.floor(r(1) * 950_000_000) + 5_000_000;
  const dias = Math.floor(r(2) * 60) - 10;
  const cierre = new Date();
  cierre.setDate(cierre.getDate() + dias);
  const pub = new Date(cierre);
  pub.setDate(pub.getDate() - 25);
  return {
    id: `lic-${1000 + i}`,
    codigo: `${1500 + i}-${10 + (i % 90)}-LP${24 + (i % 2)}`,
    nombre:
      [
        "Adquisición de equipos informáticos",
        "Servicio de mantención eléctrica integral",
        "Construcción de pabellón modular",
        "Suministro de insumos médicos",
        "Implementación plataforma de gestión documental",
        "Servicio de alimentación escolar",
        "Renovación flota vehicular",
        "Consultoría en transformación digital",
        "Mejoramiento de infraestructura vial",
        "Provisión de mobiliario clínico",
      ][i % 10] + ` — Lote ${(i % 6) + 1}`,
    organismo: organismos[i % organismos.length],
    categoria: categorias[i % categorias.length],
    region: regiones[i % regiones.length],
    montoEstimado: monto,
    moneda: "CLP",
    fechaPublicacion: pub.toISOString(),
    fechaCierre: cierre.toISOString(),
    estado: estados[i % estados.length],
    prioridad: (["alta", "media", "baja"] as Prioridad[])[i % 3],
    scoreIA: Math.floor(r(3) * 45) + 50,
    responsable: responsables[i % responsables.length],
    modalidad: (
      ["Licitación Pública", "Convenio Marco", "Trato Directo", "Licitación Privada"] as const
    )[i % 4],
    riesgo: (["bajo", "medio", "alto"] as const)[i % 3],
    documentos: Math.floor(r(4) * 12) + 1,
  };
});

export const estadoLabels: Record<LicitacionEstado, string> = {
  detectada: "Detectada",
  analisis: "En análisis",
  viable: "Viable",
  preparacion: "Preparación",
  postulada: "Postulada",
  evaluacion: "Evaluación",
  adjudicada: "Adjudicada",
  perdida: "Perdida",
  cerrada: "Cerrada",
};

export const estadoColors: Record<LicitacionEstado, string> = {
  detectada: "bg-muted text-muted-foreground",
  analisis: "bg-info/15 text-info",
  viable: "bg-primary/15 text-primary",
  preparacion: "bg-warning/15 text-warning",
  postulada: "bg-accent text-accent-foreground",
  evaluacion: "bg-chart-4/20 text-chart-4",
  adjudicada: "bg-success/15 text-success",
  perdida: "bg-destructive/15 text-destructive",
  cerrada: "bg-muted text-muted-foreground",
};

/** Estados oficiales de la publicación en Mercado Público (distintos del pipeline interno). */
export const estadoMPLabels: Record<string, string> = {
  publicada: "Publicada",
  cerrada: "Cerrada",
  desierta: "Desierta",
  adjudicada: "Adjudicada",
  revocada: "Revocada",
  suspendida: "Suspendida",
};

export const estadoMPColors: Record<string, string> = {
  publicada: "bg-info/15 text-info",
  cerrada: "bg-muted text-muted-foreground",
  desierta: "bg-warning/15 text-warning",
  adjudicada: "bg-success/15 text-success",
  revocada: "bg-destructive/15 text-destructive",
  suspendida: "bg-destructive/15 text-destructive",
};


export function formatCLP(n: number) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);
}

export function diasRestantes(iso: string) {
  const d = new Date(iso).getTime() - Date.now();
  return Math.ceil(d / (1000 * 60 * 60 * 24));
}

export const kpis: KPISet = {
  licitacionesActivas: 127,
  oportunidadesPriorizadas: 23,
  postuladasMes: 18,
  tasaAdjudicacion: 34.5,
  montoPipeline: 2_840_000_000,
  alertasVencimiento: 7,
};

export const pipelineData: PipelinePoint[] = [
  { mes: "Mar", pipeline: 1200, adjudicado: 420 },
  { mes: "Abr", pipeline: 1450, adjudicado: 510 },
  { mes: "May", pipeline: 1680, adjudicado: 580 },
  { mes: "Jun", pipeline: 1920, adjudicado: 670 },
  { mes: "Jul", pipeline: 2150, adjudicado: 720 },
  { mes: "Ago", pipeline: 2380, adjudicado: 810 },
  { mes: "Sep", pipeline: 2840, adjudicado: 970 },
];

export const categoriaData: CategoriaShare[] = [
  { name: "Tecnología", value: 38 },
  { name: "Servicios", value: 27 },
  { name: "Construcción", value: 18 },
  { name: "Médico", value: 12 },
  { name: "Otros", value: 5 },
];

export const actividadReciente: ActividadItem[] = [
  {
    id: 1,
    tipo: "adjudicacion",
    titulo: "Adjudicación confirmada",
    desc: "Hospital San Borja — Insumos médicos",
    tiempo: "hace 12 min",
    user: "Sistema",
  },
  {
    id: 2,
    tipo: "ia",
    titulo: "Análisis IA completado",
    desc: "12 nuevas licitaciones procesadas",
    tiempo: "hace 34 min",
    user: "Motor IA",
  },
  {
    id: 3,
    tipo: "alerta",
    titulo: "Cierre próximo",
    desc: "MOP — Mantención vial cierra en 2 días",
    tiempo: "hace 1 h",
    user: "Alertas",
  },
  {
    id: 4,
    tipo: "comentario",
    titulo: "Nuevo comentario",
    desc: "María G. en Licitación 1583-22-LP24",
    tiempo: "hace 2 h",
    user: "María González",
  },
  {
    id: 5,
    tipo: "documento",
    titulo: "Documento subido",
    desc: "Bases técnicas v2 — JUNAEB",
    tiempo: "hace 3 h",
    user: "Carlos Pérez",
  },
  {
    id: 6,
    tipo: "estado",
    titulo: "Cambio de estado",
    desc: "Licitación movida a Postulada",
    tiempo: "hace 5 h",
    user: "Andrea Soto",
  },
];

export const currentUser: NavUser = {
  nombre: "Andrea Soto",
  email: "andrea.soto@empresa.cl",
  empresa: "Constructora Andes SpA",
  rol: "Gerente Comercial",
  inicial: "AS",
};

export const empresas: Tenant[] = [
  { id: "e1", nombre: "Constructora Andes SpA", rut: "76.123.456-7", licitaciones: 84 },
  { id: "e2", nombre: "TechSolutions Chile", rut: "77.987.654-3", licitaciones: 43 },
];

export const documentos: Documento[] = [
  {
    id: "d1",
    nombre: "Bases Administrativas.pdf",
    tipo: "PDF",
    tamano: "2.4 MB",
    sizeBytes: 2_516_582,
    actualizado: "hace 2 días",
    carpeta: "1583-22 · Bases y anexos",
    ambito: "licitacion",
    subcarpeta: "bases-anexos",
    licitacionId: "l1",
    licitacion: { id: "l1", codigo: "1583-22", nombre: "Servicio de aseo Hospital Regional" },
    estadoCarga: "subido",
    tags: ["bases", "vigente"],
  },
  {
    id: "d2",
    nombre: "Anexo Técnico Hospital.docx",
    tipo: "DOCX",
    tamano: "1.1 MB",
    sizeBytes: 1_153_433,
    actualizado: "hace 5 días",
    carpeta: "1601-15 · Bases y anexos",
    ambito: "licitacion",
    subcarpeta: "bases-anexos",
    licitacionId: "l2",
    licitacion: { id: "l2", codigo: "1601-15", nombre: "Equipamiento clínico" },
    estadoCarga: "subido",
    tags: ["técnico"],
  },
  {
    id: "d3",
    nombre: "Certificado Vigencia SII.pdf",
    tipo: "PDF",
    tamano: "320 KB",
    sizeBytes: 327_680,
    actualizado: "ayer",
    carpeta: "Empresa · Legal",
    ambito: "empresa",
    subcarpeta: "legal",
    estadoCarga: "subido",
    tags: ["legal", "vence pronto"],
  },
  {
    id: "d4",
    nombre: "Boleta de Garantía 2024.pdf",
    tipo: "PDF",
    tamano: "180 KB",
    sizeBytes: 184_320,
    actualizado: "hace 1 sem",
    carpeta: "Empresa · Garantías",
    ambito: "empresa",
    subcarpeta: "garantias",
    estadoCarga: "subido",
    tags: ["financiero"],
  },
  {
    id: "d5",
    nombre: "Propuesta Económica v3.xlsx",
    tipo: "XLSX",
    tamano: "640 KB",
    sizeBytes: 655_360,
    actualizado: "hace 3 h",
    carpeta: "1612-08 · Propuesta",
    ambito: "licitacion",
    subcarpeta: "propuesta",
    licitacionId: "l3",
    licitacion: { id: "l3", codigo: "1612-08", nombre: "Mantención de redes" },
    estadoCarga: "subido",
    tags: ["propuesta"],
  },
  {
    id: "d6",
    nombre: "Currículum Empresa.pdf",
    tipo: "PDF",
    tamano: "4.2 MB",
    sizeBytes: 4_404_019,
    actualizado: "hace 1 mes",
    carpeta: "Empresa · Certificaciones",
    ambito: "empresa",
    subcarpeta: "certificaciones",
    estadoCarga: "subido",
    tags: ["comercial"],
  },
];

export const notificaciones: Notificacion[] = [
  {
    id: "n1",
    titulo: "Licitación 1583-22 cierra en 48h",
    tipo: "alerta",
    leida: false,
    tiempo: "hace 10 min",
  },
  {
    id: "n2",
    titulo: "IA detectó 5 nuevas oportunidades",
    tipo: "info",
    leida: false,
    tiempo: "hace 1 h",
  },
  {
    id: "n3",
    titulo: "Adjudicación: Hospital San Borja",
    tipo: "success",
    leida: false,
    tiempo: "hace 3 h",
  },
  {
    id: "n4",
    titulo: "Documento próximo a vencer: Certificado SII",
    tipo: "warning",
    leida: true,
    tiempo: "ayer",
  },
  {
    id: "n5",
    titulo: "Carlos Pérez te asignó una tarea",
    tipo: "info",
    leida: true,
    tiempo: "hace 2 días",
  },
];

export const usuariosAdmin: User[] = [
  {
    id: "u1",
    nombre: "Andrea Soto",
    email: "andrea.soto@empresa.cl",
    rol: "Admin",
    estado: "activo",
    ultimoAcceso: "hace 5 min",
  },
  {
    id: "u2",
    nombre: "Carlos Pérez",
    email: "carlos.perez@empresa.cl",
    rol: "Editor",
    estado: "activo",
    ultimoAcceso: "hace 1 h",
  },
  {
    id: "u3",
    nombre: "María González",
    email: "maria.gonzalez@empresa.cl",
    rol: "Gerente",
    estado: "activo",
    ultimoAcceso: "hace 3 h",
  },
  {
    id: "u4",
    nombre: "Felipe Rojas",
    email: "felipe.rojas@empresa.cl",
    rol: "Analista",
    estado: "activo",
    ultimoAcceso: "ayer",
  },
  {
    id: "u5",
    nombre: "Camila Vidal",
    email: "camila.vidal@empresa.cl",
    rol: "Viewer",
    estado: "inactivo",
    ultimoAcceso: "hace 2 sem",
  },
];
